import React, {useEffect, useState} from 'react';
import {
    CalendarClock, CalendarDays, CalendarOff, CalendarX2, Check, CheckCircle2, Clock, MessageCircle, Plus, Scissors,
    Search, StickyNote, Trash2, TreePalm, User, UserCog, X
} from 'lucide-react';
import IOSSwitch from '../components/Common/IOSSwitch';
import ReferencePickerModal from '../components/Common/ReferencePickerModal';
import {clientsConfig, servicesConfig} from '../components/Common/referenceConfigs';
import {Avatar, Modal, ModalFooter, RowActions, thClass, mobileRowClass, ListEmpty} from '../stock/StockUI';
import Button from '../components/Common/Button';
import {inputClass} from '../ClientsTable/clientFormUI';
import {STATUS_OPTIONS, statusLabel, statusStyle} from './calendarColors';
import {
    ALL_WEEKDAYS, REST_FULL_DAY, WEEKDAY_CHIPS, findRestConflict, formatDecimalHour, keyedSlot, restDaysForDay,
    restHoursLabel, restKindLabel, restSlotsError
} from './restDays';
import DateInput from "../components/Common/DateInput";

// Calendar popups, in the catalogue popup style (centred title, "Annuler | Action"
// footer) with the calendar's form look: uppercase section titles with a teal
// icon, rounded-xl fields, professional chips and an iOS segmented status.
// They live at module level: declared inside the calendar they were recreated
// on every refresh of the page (sync, notifications), which wiped whatever was
// being typed.

const fieldClass = inputClass(false);

const clientFullName = (c) =>
    `${c.firstName || c.first_name || ''} ${c.lastName || c.last_name || ''}`.trim();

const pad2 = n => String(n).padStart(2, '0');
const toDateInput = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const toTimeInput = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const minutesBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 60000);
const addMinutes = (d, min) => new Date(new Date(d).getTime() + min * 60000);
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
const formatDuration = (min) => {
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60), m = min % 60;
    return m ? `${h} h ${pad2(m)}` : `${h} h`;
};

const QUICK_DURATIONS = [15, 30, 45, 60, 90, 120];
const PROFESSIONAL_ACCENT_RED = '#E11D48';

// ─── Shared form pieces ────────────────────────────────────────────────────────

const pickerButtonClass =
    'w-full flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-sm text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors';

const subLabelClass = 'text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1';

// Uppercase section title with a teal icon, like the Panel headers
const SectionTitle = ({icon: Icon, children, required, aside}) => (
    <div className="flex items-center justify-between gap-2 mb-2.5 min-h-[2rem]">
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={2.25}/>
            {children}{required && <span className="text-red-500">*</span>}
        </h4>
        {aside}
    </div>
);

// Form body: sections split by a hairline
const FormSections = ({children}) => (
    <div className="px-6 py-2 divide-y divide-gray-200 dark:divide-gray-700">{children}</div>
);
const FormSection = ({className = '', children}) => <section className={`py-4 ${className}`}>{children}</section>;

// Selected client row / "search a client" button
const ClientField = ({clientName, onPick, onClear}) => (
    clientName ? (
        <div className="flex items-center gap-3 p-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40">
            <Avatar name={clientName} size="sm"/>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate flex-1">{clientName}</span>
            <Button variant="plain" size="sm" onClick={onPick}>Changer</Button>
            <button type="button" aria-label="Retirer le client" onClick={onClear}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-600 flex-shrink-0">
                <X className="w-4 h-4"/>
            </button>
        </div>
    ) : (
        <button type="button" onClick={onPick} className={pickerButtonClass}>
            <Search className="w-4 h-4"/>
            Rechercher un client...
        </button>
    )
);

// Professional chips (colour avatar + name), same look as the calendar filter bar.
// `value` is one id, or an array of ids for a multi-select; `badges` maps an id
// to a small warning tag ("Repos", "Congé").
const ProfessionalPicker = ({resources, value, onChange, badges = {}}) => (
    <div className="flex flex-wrap gap-2">
        {resources.map(r => {
            const selected = Array.isArray(value)
                ? value.some(v => String(v) === String(r.id))
                : String(r.id) === String(value);
            const badge = badges[r.id];
            return (
                <button key={r.id} type="button" aria-pressed={selected} onClick={() => onChange(r.id)}
                        className={`flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-xl text-sm font-medium border transition-all ${
                            selected
                                ? 'text-gray-900 dark:text-white shadow-sm'
                                : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                        style={selected ? {borderColor: r.color, backgroundColor: `${r.color}1F`} : undefined}>
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs text-white flex-shrink-0"
                          style={{backgroundColor: r.color}}>
                        {selected ? <Check className="w-4 h-4" strokeWidth={3}/> : (r.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                        <span className="whitespace-nowrap">{r.name}</span>
                        {badge
                            ? <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">{badge}</span>
                            : r.specialty && <span className="text-[11px] opacity-60 whitespace-nowrap">{r.specialty}</span>}
                    </span>
                </button>
            );
        })}
    </div>
);

// iOS-style segmented control. options: [{value, label, dot?}]
export const Segmented = ({options, value, onChange, columns = 'grid-cols-2 sm:grid-cols-4'}) => (
    <div role="radiogroup" className={`grid ${columns} gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-900/60`}>
        {options.map(opt => {
            const selected = value === opt.value;
            return (
                <button key={opt.value} type="button" role="radio" aria-checked={selected} onClick={() => onChange(opt.value)}
                        className={`flex items-center justify-center gap-1.5 py-1.5 sm:py-2 px-2 rounded-lg text-sm font-medium transition-all ${
                            selected
                                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                        }`}>
                    {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot}`}/>}
                    {opt.label}
                </button>
            );
        })}
    </div>
);

const STATUS_SEGMENTS = STATUS_OPTIONS.map(o => ({...o, dot: statusStyle(o.value).dot}));

// Selectable service tiles (waiting list), or the chosen services list (appointment)
const ServiceTiles = ({services, selected, onToggle}) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {services.map(service => {
            const on = selected.includes(service.id);
            return (
                <button key={service.id} type="button" aria-pressed={on} onClick={() => onToggle(service.id)}
                        className={`flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all ${
                            on
                                ? 'border-primary bg-primary/10 shadow-sm'
                                : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}>
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        on ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
                    }`}>
                        {on ? <Check className="w-4 h-4" strokeWidth={3}/> : <Scissors className="w-3.5 h-3.5"/>}
                    </span>
                    <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{service.name}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 tabular-nums">{formatDuration(service.duration || 0)}</span>
                    </span>
                </button>
            );
        })}
    </div>
);

// Coloured header card summing up the slot (date, time, pro, status)
const SlotSummary = ({icon: Icon, color, initial, title, subtitle, status}) => {
    const st = status && statusStyle(status);
    return (
        <div className="px-6 pt-5">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-primary-lighter/70 dark:bg-primary/10 border border-primary/20">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold shadow-sm flex-shrink-0"
                     style={{backgroundColor: color || '#4DB6AC'}}>
                    {Icon ? <Icon className="w-5 h-5"/> : initial}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 tabular-nums truncate">{subtitle}</div>
                </div>
                {st && (
                    <span className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${st.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>
                        {statusLabel(status)}
                    </span>
                )}
            </div>
        </div>
    );
};

// ─── Waiting list: add ─────────────────────────────────────────────────────────

export const WaitingListModal = ({onClose, onSave, services, resources, clients}) => {
    const [formData, setFormData] = useState({
        clientId: '',
        clientName: '',
        employeeId: resources[0]?.id || '',
        date: new Date(),
        notes: '',
        status: 'pending',
        selectedServices: []
    });
    const [showClientPicker, setShowClientPicker] = useState(false);

    const set = (changes) => setFormData(prev => ({...prev, ...changes}));
    const employee = resources.find(r => String(r.id) === String(formData.employeeId));
    const totalMinutes = formData.selectedServices
        .reduce((acc, id) => acc + (services.find(s => s.id === id)?.duration || 0), 0);

    const handleSubmit = () => {
        onSave({
            ...formData,
            employeeName: employee ? employee.name : '',
            date: new Date(formData.date)
        });
    };

    const toggleService = (serviceId) => set({
        selectedServices: formData.selectedServices.includes(serviceId)
            ? formData.selectedServices.filter(id => id !== serviceId)
            : [...formData.selectedServices, serviceId]
    });

    return (
        <>
            <Modal
                title="Ajouter à la file d'attente"
                subtitle="Ajoutez un client à la liste d'attente"
                onClose={onClose}
                size="lg"
                bodyClass=""
                footer={<ModalFooter onCancel={onClose} onConfirm={handleSubmit} disabled={!formData.clientId} label="Ajouter à la file"/>}
            >
                <SlotSummary
                    icon={CalendarClock}
                    title={capitalize(formData.date.toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'}))}
                    subtitle={[
                        formData.clientName || 'Aucun client',
                        employee?.name,
                        totalMinutes > 0 && formatDuration(totalMinutes),
                    ].filter(Boolean).join(' · ')}
                />

                <FormSections>
                    <FormSection>
                        <SectionTitle icon={User} required>Client</SectionTitle>
                        <ClientField clientName={formData.clientName}
                                     onPick={() => setShowClientPicker(true)}
                                     onClear={() => set({clientId: '', clientName: ''})}/>
                    </FormSection>

                    <FormSection>
                        <SectionTitle icon={UserCog}>Employé demandé</SectionTitle>
                        <ProfessionalPicker resources={resources} value={formData.employeeId}
                                            onChange={(id) => set({employeeId: id})}/>
                    </FormSection>

                    <FormSection>
                        <SectionTitle icon={Scissors}
                                      aside={totalMinutes > 0 && (
                                          <span className="text-xs font-semibold text-primary tabular-nums">{formatDuration(totalMinutes)}</span>
                                      )}>
                            Prestations demandées
                        </SectionTitle>
                        {services.length === 0
                            ? <p className="text-sm text-gray-500 dark:text-gray-400">Aucune prestation disponible.</p>
                            : <ServiceTiles services={services} selected={formData.selectedServices} onToggle={toggleService}/>}
                    </FormSection>

                    <FormSection className="space-y-3">
                        <SectionTitle icon={StickyNote}>Détails</SectionTitle>
                        <div className="sm:w-1/2 sm:pr-1.5">
                            <label htmlFor="wl-date" className={subLabelClass}>Date souhaitée</label>
                            <DateInput id="wl-date" value={toDateInput(formData.date)}
                                   onChange={(e) => {
                                       if (!e.target.value) return;
                                       const [y, m, d] = e.target.value.split('-').map(Number);
                                       set({date: new Date(y, m - 1, d)});
                                   }}
                                   className={fieldClass}/>
                        </div>
                        <div>
                            <label htmlFor="wl-notes" className={subLabelClass}>Notes</label>
                            <textarea id="wl-notes" value={formData.notes}
                                      onChange={(e) => set({notes: e.target.value})}
                                      className={`${fieldClass} resize-none`}
                                      rows="3" placeholder="Notes supplémentaires..."/>
                        </div>
                    </FormSection>
                </FormSections>
            </Modal>

            {showClientPicker && (
                <ReferencePickerModal
                    config={clientsConfig}
                    currentValue={formData.clientId || null}
                    onSelect={(item) => {
                        const fromList = clients.find(c => c.id === item.id);
                        const name = fromList ? clientFullName(fromList)
                            : clientsConfig.getName ? clientsConfig.getName(item) : (item.name || '—');
                        set({clientId: item.id, clientName: name});
                        setShowClientPicker(false);
                    }}
                    onClose={() => setShowClientPicker(false)}
                />
            )}
        </>
    );
};

// ─── Waiting list: view ────────────────────────────────────────────────────────

export const WaitingListView = ({waitingList, onClose, onSchedule, onAdd, onDelete, services}) => {
    const serviceNames = (item) => item.selectedServices
        .map(id => services.find(s => s.id === id)?.name)
        .filter(Boolean);

    const actions = (item) => [
        {label: 'Planifier le RDV', icon: CalendarClock, onClick: () => onSchedule(item)},
        {label: 'Supprimer', icon: Trash2, onClick: () => onDelete(item.id), danger: true},
    ];

    const ServiceChips = ({item}) => (
        <span className="flex flex-wrap gap-1">
            {serviceNames(item).map(name => (
                <span key={name} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary-dark dark:text-primary">
                    <Scissors className="w-3 h-3"/>{name}
                </span>
            ))}
        </span>
    );

    return (
        <Modal
            title="File d'attente"
            subtitle={waitingList.length === 0
                ? 'Clients en attente de rendez-vous'
                : `${waitingList.length} client${waitingList.length > 1 ? 's' : ''} en attente de rendez-vous`}
            onClose={onClose}
            size="xl"
            bodyClass=""
            footer={(
                <div className="grid grid-cols-2 border-t border-gray-200 dark:border-gray-700">
                    <button type="button" onClick={onClose}
                            className="py-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                        Fermer
                    </button>
                    <button type="button" onClick={onAdd}
                            className="py-4 font-medium text-primary border-l border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-2">
                        <Plus className="w-4 h-4"/>
                        Ajouter un client
                    </button>
                </div>
            )}
        >
            {waitingList.length === 0 ? (
                <ListEmpty icon={User} title="La file d'attente est vide" text="Ajoutez les clients qui attendent un créneau."/>
            ) : (
                <>
                    <table className="hidden md:table min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            <th className={thClass}>Client</th>
                            <th className={thClass}>Employé</th>
                            <th className={thClass}>Prestations</th>
                            <th className={thClass}>Souhaitée</th>
                            <th className={thClass}>Notes</th>
                            <th className={`${thClass} text-right`}>Actions</th>
                        </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {waitingList.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <Avatar name={item.clientName || '?'} size="sm"/>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">{item.clientName}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                                                Ajouté le {item.createdAt.toLocaleDateString('fr-FR')}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">{item.employeeName}</td>
                                <td className="px-6 py-4"><ServiceChips item={item}/></td>
                                <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap tabular-nums">
                                    {item.date.toLocaleDateString('fr-FR')}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-[12rem] truncate">{item.notes || '—'}</td>
                                <td className="px-6 py-4 text-right"><RowActions actions={actions(item)}/></td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/* Mobile : lignes pleine largeur, comme les listes du Stock / catalogue */}
                    <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                        {waitingList.map((item) => (
                            <div key={item.id} className={mobileRowClass}>
                                <div className="flex items-start gap-3">
                                    <Avatar name={item.clientName || '?'} size="sm"/>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{item.clientName}</h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {item.employeeName} · souhaitée le {item.date.toLocaleDateString('fr-FR')}
                                        </p>
                                        <div className="mt-2"><ServiceChips item={item}/></div>
                                        {item.notes && <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{item.notes}</p>}
                                    </div>
                                    <RowActions actions={actions(item)}/>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </Modal>
    );
};

// ─── Appointment: create / edit ────────────────────────────────────────────────

export const EventModal = ({
    event = null, onClose, onSave, onDelete,
    selectedTimeSlot, selectedResource,
    resources, clients, services,
    isEdit = false, initialClientId = null,
    title,          // overrides "Nouveau / Modifier le rendez-vous"
    actions = [],   // quick actions of an existing RDV (ActionMenu items), shown under the summary
    restDays = [],  // employees' Repos / Congé — a slot falling in one can't be booked (like the tablets)
}) => {
    const [formData, setFormData] = useState(() => {
        const start = new Date(event?.start || selectedTimeSlot?.start || new Date());
        if (!event && !selectedTimeSlot) {
            // "Nouveau" without a slot: next quarter hour
            start.setMinutes(Math.ceil(start.getMinutes() / 15) * 15, 0, 0);
        }
        const end = new Date(event?.end || selectedTimeSlot?.end || addMinutes(start, 30));
        return {
            title:      event?.title      || '',
            clientId:   event?.clientId   || initialClientId || '',
            clientName: event?.clientName || '',
            resourceId: event?.resourceId || selectedResource || resources[0]?.id || '',
            start,
            end,
            status:     event?.status     || 'confirmed',
            notes:      event?.notes      || '',
            color:      event?.color      || '#4DB6AC',
        };
    });

    // Active picker: null | 'client' | 'services'
    const [activePicker, setActivePicker] = useState(null);

    // Full service objects selected (not just IDs); objects returned by
    // ReferencePickerModal carry {id, name, duration} directly.
    const [selectedServiceObjects, setSelectedServiceObjects] = useState(() => {
        if (!event?.selectedServices?.length) return [];
        return event.selectedServices.map(sid => services.find(s => s.id === sid) || {id: sid, name: sid, duration: 0});
    });

    // Pre-fill client when converting a waiting-list entry
    useEffect(() => {
        if (initialClientId && !event?.clientId) {
            const c = clients.find(cl => cl.id === initialClientId);
            if (c) setFormData(prev => ({...prev, clientId: c.id, clientName: clientFullName(c)}));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialClientId, clients]);

    const servicesMinutes = selectedServiceObjects.reduce((acc, svc) => acc + (svc.duration || 0), 0);

    // Auto-adjust end time based on selected services
    useEffect(() => {
        if (servicesMinutes > 0) {
            setFormData(prev => ({...prev, end: addMinutes(prev.start, servicesMinutes)}));
        }
    }, [servicesMinutes, formData.start]);

    const set = (changes) => setFormData(prev => ({...prev, ...changes}));

    const duration = minutesBetween(formData.start, formData.end);
    const invalidTime = isNaN(duration) || duration <= 0;
    const resource = resources.find(r => String(r.id) === String(formData.resourceId));
    const restConflict = invalidTime ? null : findRestConflict(restDays, formData.resourceId, formData.start, formData.end);
    // Pros resting / on leave that day, flagged on their chip
    const restBadges = {};
    restDaysForDay(restDays, formData.start).forEach(r => {
        restBadges[r.employee_id] = `${restKindLabel(r)} · ${restHoursLabel(r)}`;
    });

    // Date / start keep the current duration; the end stays on the start's day
    const setDay = (value) => {
        if (!value) return;
        const [y, m, d] = value.split('-').map(Number);
        const start = new Date(formData.start);
        start.setFullYear(y, m - 1, d);
        set({start, end: addMinutes(start, Math.max(duration, 15))});
    };
    const setStartTime = (value) => {
        if (!value) return;
        const [h, mi] = value.split(':').map(Number);
        const start = new Date(formData.start);
        start.setHours(h, mi, 0, 0);
        set({start, end: addMinutes(start, Math.max(duration, 15))});
    };
    const setEndTime = (value) => {
        if (!value) return;
        const [h, mi] = value.split(':').map(Number);
        const end = new Date(formData.start);
        end.setHours(h, mi, 0, 0);
        set({end});
    };

    const handleSubmit = () => {
        if (invalidTime || restConflict) return;
        onSave({
            ...formData,
            id: event?.id,
            // IDs for backward-compat
            selectedServices: selectedServiceObjects.map(s => s.id),
            // Names used by createEvent / updateEvent
            _serviceNames: selectedServiceObjects.map(s => s.name),
            start: new Date(formData.start),
            end:   new Date(formData.end),
        });
    };

    return (
        <>
            <Modal
                title={title || (isEdit ? 'Modifier le rendez-vous' : 'Nouveau rendez-vous')}
                subtitle={isEdit ? formData.clientName || event?.title : undefined}
                onClose={onClose}
                size="lg"
                bodyClass=""
                footer={<ModalFooter onCancel={onClose} onConfirm={handleSubmit} disabled={invalidTime || !!restConflict}
                                     label={isEdit ? 'Enregistrer' : 'Créer le RDV'}/>}
            >
                <SlotSummary
                    color={resource?.color}
                    initial={(resource?.name || '?').charAt(0).toUpperCase()}
                    title={capitalize(formData.start.toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'}))}
                    subtitle={[
                        `${toTimeInput(formData.start)} – ${toTimeInput(formData.end)}`,
                        !invalidTime && formatDuration(duration),
                        resource?.name,
                    ].filter(Boolean).join(' · ')}
                    status={formData.status}
                />

                {actions.some(a => !a.divider) && (
                    <div className="px-6 pt-3 flex flex-wrap gap-2">
                        {actions.filter(a => !a.divider).map(a => (
                            <Button key={a.label} size="sm" variant={a.danger ? 'danger' : 'tinted'} icon={a.icon} onClick={a.onClick}>
                                {a.label}
                            </Button>
                        ))}
                    </div>
                )}

                <FormSections>
                    <FormSection>
                        <SectionTitle icon={User} required>Client</SectionTitle>
                        <ClientField clientName={formData.clientId ? formData.clientName || 'Client' : ''}
                                     onPick={() => setActivePicker('client')}
                                     onClear={() => set({clientId: '', clientName: ''})}/>
                    </FormSection>

                    <FormSection>
                        <SectionTitle icon={UserCog} required>Professionnel</SectionTitle>
                        <ProfessionalPicker resources={resources} value={formData.resourceId} badges={restBadges}
                                            onChange={(id) => set({resourceId: id})}/>
                        {restConflict && (
                            <div role="alert" className="mt-3 flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-sm text-amber-800 dark:text-amber-300">
                                <CalendarOff className="w-4 h-4 mt-0.5 flex-shrink-0"/>
                                <span>
                                    <span className="font-semibold">Créneau indisponible — {restKindLabel(restConflict).toLowerCase()} de {resource?.name || 'cet employé'}</span>
                                    <span className="block text-xs opacity-80">
                                        {restHoursLabel(restConflict)} · du {formatYmd(restConflict.start_date)} au {formatYmd(restConflict.end_date)}.
                                        Choisissez un autre horaire ou un autre professionnel.
                                    </span>
                                </span>
                            </div>
                        )}
                    </FormSection>

                    <FormSection>
                        <SectionTitle icon={Clock}
                                      aside={!invalidTime && (
                                          <span className="text-xs font-semibold text-primary tabular-nums">{formatDuration(duration)}</span>
                                      )}>
                            Horaire
                        </SectionTitle>
                        <div className="grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_1fr] gap-3">
                            <div className="col-span-2 sm:col-span-1">
                                <label htmlFor="ev-day" className={subLabelClass}>Date</label>
                                <DateInput id="ev-day" value={toDateInput(formData.start)}
                                       onChange={e => setDay(e.target.value)} className={fieldClass}/>
                            </div>
                            <div>
                                <label htmlFor="ev-start" className={subLabelClass}>Début</label>
                                <input id="ev-start" type="time" step={300} value={toTimeInput(formData.start)}
                                       onChange={e => setStartTime(e.target.value)} className={`${fieldClass} tabular-nums`}/>
                            </div>
                            <div>
                                <label htmlFor="ev-end" className={subLabelClass}>Fin</label>
                                <input id="ev-end" type="time" step={300} value={toTimeInput(formData.end)}
                                       onChange={e => setEndTime(e.target.value)}
                                       className={`${inputClass(invalidTime)} tabular-nums`}/>
                            </div>
                        </div>
                        {invalidTime ? (
                            <p className="text-red-500 text-xs sm:text-sm mt-2">L'heure de fin doit être après l'heure de début.</p>
                        ) : servicesMinutes > 0 ? (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Durée calculée à partir des prestations.</p>
                        ) : (
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {QUICK_DURATIONS.map(min => (
                                    <button key={min} type="button" onClick={() => set({end: addMinutes(formData.start, min)})}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold tabular-nums transition-colors ${
                                                duration === min
                                                    ? 'bg-primary text-white shadow-sm shadow-primary/30'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary'
                                            }`}>
                                        {formatDuration(min)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </FormSection>

                    <FormSection>
                        <SectionTitle icon={Scissors}
                                      aside={selectedServiceObjects.length > 0 && (
                                          <Button variant="plain" size="sm" icon={Plus} onClick={() => setActivePicker('services')}>Modifier</Button>
                                      )}>
                            Prestations
                        </SectionTitle>
                        {selectedServiceObjects.length > 0 ? (
                            <div className="rounded-xl border border-gray-200 dark:border-gray-600 divide-y divide-gray-200 dark:divide-gray-600 overflow-hidden">
                                {selectedServiceObjects.map(svc => (
                                    <div key={svc.id} className="flex items-center gap-3 px-3 py-2 bg-white dark:bg-gray-800">
                                        <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                            <Scissors className="w-3.5 h-3.5"/>
                                        </span>
                                        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{svc.name}</span>
                                        {svc.duration > 0 && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{formatDuration(svc.duration)}</span>
                                        )}
                                        <button type="button" aria-label={`Retirer ${svc.name}`}
                                                onClick={() => setSelectedServiceObjects(p => p.filter(x => x.id !== svc.id))}
                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-500/10">
                                            <X className="w-4 h-4"/>
                                        </button>
                                    </div>
                                ))}
                                {servicesMinutes > 0 && (
                                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700/40 text-xs font-semibold text-gray-600 dark:text-gray-300">
                                        <span>Total</span>
                                        <span className="tabular-nums">{formatDuration(servicesMinutes)}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button type="button" onClick={() => setActivePicker('services')} className={pickerButtonClass}>
                                <Plus className="w-4 h-4"/>
                                Ajouter des prestations...
                            </button>
                        )}
                    </FormSection>

                    <FormSection>
                        <SectionTitle icon={CheckCircle2}>Statut</SectionTitle>
                        <Segmented options={STATUS_SEGMENTS} value={formData.status} onChange={(status) => set({status})}/>
                    </FormSection>

                    <FormSection className="space-y-3">
                        <SectionTitle icon={StickyNote}>Détails</SectionTitle>
                        <div>
                            <label htmlFor="ev-title" className={subLabelClass}>Titre</label>
                            <input id="ev-title" type="text" value={formData.title}
                                   onChange={e => set({title: e.target.value})}
                                   className={fieldClass} placeholder="Ex: Coupe de cheveux"/>
                        </div>
                        <div>
                            <label htmlFor="ev-notes" className={subLabelClass}>Notes</label>
                            <textarea id="ev-notes" value={formData.notes}
                                      onChange={e => set({notes: e.target.value})}
                                      className={`${fieldClass} resize-none`}
                                      rows={3} placeholder="Notes supplémentaires..."/>
                        </div>
                    </FormSection>

                    {isEdit && onDelete && (
                        <FormSection>
                            <Button variant="danger" size="sm" icon={Trash2} onClick={onDelete}>
                                Supprimer le rendez-vous
                            </Button>
                        </FormSection>
                    )}
                </FormSections>
            </Modal>

            {activePicker === 'client' && (
                <ReferencePickerModal
                    config={clientsConfig}
                    currentValue={formData.clientId || null}
                    onSelect={(item) => {
                        const name = clientsConfig.getName ? clientsConfig.getName(item) : (item.name || '—');
                        set({clientId: item.id, clientName: name});
                        setActivePicker(null);
                    }}
                    onClose={() => setActivePicker(null)}
                />
            )}

            {activePicker === 'services' && (
                <ReferencePickerModal
                    config={servicesConfig}
                    multiSelect={true}
                    initialSelected={selectedServiceObjects}
                    onSelect={(selectedItems) => {
                        setSelectedServiceObjects(selectedItems.map(svc => ({
                            id: svc.id,
                            name: svc.name,
                            duration: svc.duration || 0,
                        })));
                        setActivePicker(null);
                    }}
                    onClose={() => setActivePicker(null)}
                />
            )}
        </>
    );
};

// ─── Employees' rest days / leave ──────────────────────────────────────────────

const REST_KINDS = [{value: 'repos', label: 'Repos'}, {value: 'conge', label: 'Congé'}];

const hourToInput = (value) => formatDecimalHour(value);
const inputToHour = (value) => {
    const [h, m] = value.split(':').map(Number);
    return h + m / 60;
};
const formatYmd = (value) => (value ? value.split('-').reverse().join('/') : '');
const daysBetween = (from, to) =>
    Math.round((new Date(`${to}T00:00:00`) - new Date(`${from}T00:00:00`)) / 86400000) + 1;

// One "days + hours" block of the Repos form. `title` / `onRemove` only when there are several.
const RestSlotFields = ({slot, title, onChange, onRemove}) => {
    const fullDay = slot.start_time <= REST_FULL_DAY.start_time && slot.end_time >= REST_FULL_DAY.end_time;
    const toggleDay = (id) => onChange({
        days_of_week: slot.days_of_week.includes(id)
            ? slot.days_of_week.filter(d => d !== id)
            : [...slot.days_of_week, id],
    });

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
            {onRemove && (
                <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{title}</span>
                    <button type="button" aria-label={`Retirer le ${title.toLowerCase()}`} onClick={onRemove}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-600 flex-shrink-0">
                        <X className="w-4 h-4"/>
                    </button>
                </div>
            )}
            <div className="flex flex-wrap gap-1.5">
                {WEEKDAY_CHIPS.map(day => {
                    const on = slot.days_of_week.includes(day.id);
                    return (
                        <button key={day.id} type="button" aria-pressed={on} onClick={() => toggleDay(day.id)}
                                className={`w-12 py-1.5 rounded-xl text-sm font-medium transition-all ${
                                    on
                                        ? 'bg-primary text-white shadow-sm shadow-primary/30'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary'
                                }`}>
                            {day.short}
                        </button>
                    );
                })}
            </div>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
                <span className="text-sm text-gray-700 dark:text-gray-300">Journée entière</span>
                <IOSSwitch label="Journée entière" checked={fullDay}
                           onChange={(v) => onChange(v ? REST_FULL_DAY : {start_time: 9, end_time: 17})}/>
            </label>
            {!fullDay && (
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor={`rest-start-${slot.key}`} className={subLabelClass}>De</label>
                        <input id={`rest-start-${slot.key}`} type="time" step={900} min="09:00" max="23:00"
                               value={hourToInput(slot.start_time)} className={`${fieldClass} tabular-nums`}
                               onChange={(e) => e.target.value && onChange({start_time: inputToHour(e.target.value)})}/>
                    </div>
                    <div>
                        <label htmlFor={`rest-end-${slot.key}`} className={subLabelClass}>À</label>
                        <input id={`rest-end-${slot.key}`} type="time" step={900} min="09:00" max="23:00"
                               value={hourToInput(slot.end_time)} className={`${fieldClass} tabular-nums`}
                               onChange={(e) => e.target.value && onChange({end_time: inputToHour(e.target.value)})}/>
                    </div>
                </div>
            )}
        </div>
    );
};

// Create / edit a "Repos" (one or several slots of weekdays + hours, e.g. Lun, Mar
// 10h-19h and Mer 9h-20h) or a "Congé" (every day between two dates). `initial`:
// {employee_ids, start_date, end_date, recurring, slots: [{days_of_week, start_time, end_time}]}.
export const RestDayModal = ({initial, resources, editing = false, saving = false, onSave, onDelete, onClose}) => {
    const [kind, setKind] = useState(
        initial.slots.length === 1 && initial.slots[0].days_of_week?.length === 7 ? 'conge' : 'repos'
    );
    const [form, setForm] = useState(() => ({...initial, slots: initial.slots.map(keyedSlot)}));
    const set = (changes) => setForm(prev => ({...prev, ...changes}));
    const toggleEmployee = (id) => set({
        employee_ids: form.employee_ids.includes(id)
            ? form.employee_ids.filter(v => v !== id)
            : [...form.employee_ids, id],
    });
    const updateSlot = (key, changes) =>
        set({slots: form.slots.map(s => (s.key === key ? {...s, ...changes} : s))});
    const removeSlot = (key) => set({slots: form.slots.filter(s => s.key !== key)});
    // The next slot starts with no day and the previous slot's hours
    const addSlot = () => {
        const {start_time, end_time} = form.slots[form.slots.length - 1];
        set({slots: [...form.slots, keyedSlot({days_of_week: [], start_time, end_time})]});
    };

    const leave = kind === 'conge';
    const employees = resources.filter(r => form.employee_ids.some(id => String(id) === String(r.id)));

    const error =
        (form.employee_ids.length === 0 && 'Choisissez au moins un employé') ||
        (!leave && restSlotsError(form.slots)) ||
        ((!form.start_date || !form.end_date) && 'Dates requises') ||
        (form.end_date < form.start_date && 'La date de fin doit être après la date de début') ||
        null;

    const payload = () => (leave
        ? {...form, slots: [{days_of_week: ALL_WEEKDAYS, ...REST_FULL_DAY}], recurring: false}
        : form);

    const period = form.start_date && form.end_date && form.end_date >= form.start_date
        ? `Du ${formatYmd(form.start_date)} au ${formatYmd(form.end_date)} · ${daysBetween(form.start_date, form.end_date)} jour(s)`
        : 'Période à définir';

    return (
        <Modal
            title={editing ? `Modifier le ${leave ? 'congé' : 'repos'}` : leave ? 'Nouveau congé' : 'Nouveau repos'}
            subtitle="Envoyé aux caisses : aucun RDV ne pourra être pris sur ce créneau"
            onClose={onClose}
            size="lg"
            bodyClass=""
            footer={<ModalFooter onCancel={onClose} onConfirm={() => onSave(payload())} saving={saving}
                                 disabled={!!error} label="Enregistrer"/>}
        >
            <SlotSummary
                icon={leave ? TreePalm : CalendarOff}
                color={employees[0]?.color || '#6B7280'}
                title={employees.length ? `${leave ? 'Congé' : 'Repos'} · ${employees.map(e => e.name).join(', ')}` : (leave ? 'Congé' : 'Repos')}
                subtitle={leave ? period : `${period}${form.recurring ? ' · chaque semaine' : ''}`}
            />

            <FormSections>
                <FormSection>
                    <SectionTitle icon={CalendarOff}>Type</SectionTitle>
                    <Segmented options={REST_KINDS} value={kind} onChange={setKind} columns="grid-cols-2"/>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {leave
                            ? "Absence sur toute la période (vacances, maladie…), tous les jours de la semaine, journée entière."
                            : 'Jours de repos dans la semaine (ex. tous les lundis), sur une période donnée.'}
                    </p>
                </FormSection>

                <FormSection>
                    <SectionTitle icon={UserCog} required>{editing ? 'Employé' : 'Employés'}</SectionTitle>
                    <ProfessionalPicker resources={resources}
                                        value={form.employee_ids}
                                        onChange={(id) => (editing ? set({employee_ids: [id]}) : toggleEmployee(id))}/>
                </FormSection>

                <FormSection>
                    <SectionTitle icon={CalendarDays} required
                                  aside={form.start_date && form.end_date && form.end_date >= form.start_date && (
                                      <span className="text-xs font-semibold text-primary tabular-nums">
                                          {daysBetween(form.start_date, form.end_date)} jour(s)
                                      </span>
                                  )}>
                        Période
                    </SectionTitle>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label htmlFor="rest-from" className={subLabelClass}>Du</label>
                            <DateInput id="rest-from" className={fieldClass} value={form.start_date}
                                   onChange={(e) => set({start_date: e.target.value})}/>
                        </div>
                        <div>
                            <label htmlFor="rest-to" className={subLabelClass}>Au</label>
                            <DateInput id="rest-to" className={fieldClass} value={form.end_date} min={form.start_date}
                                   onChange={(e) => set({end_date: e.target.value})}/>
                        </div>
                    </div>
                    {!leave && (
                        <label className="flex items-center justify-between gap-3 mt-3 cursor-pointer">
                            <span className="text-sm text-gray-700 dark:text-gray-300">Récurrent (chaque semaine)</span>
                            <IOSSwitch label="Récurrent" checked={!!form.recurring} onChange={(v) => set({recurring: v})}/>
                        </label>
                    )}
                </FormSection>

                {!leave && (
                    <FormSection>
                        <SectionTitle icon={Clock} required>Jours et horaires</SectionTitle>
                        <div className="space-y-3">
                            {form.slots.map((slot, index) => (
                                <RestSlotFields key={slot.key} slot={slot}
                                                title={`Créneau ${index + 1}`}
                                                onChange={(changes) => updateSlot(slot.key, changes)}
                                                onRemove={form.slots.length > 1 ? () => removeSlot(slot.key) : undefined}/>
                            ))}
                        </div>
                        <Button variant="plain" size="sm" icon={Plus} onClick={addSlot} className="mt-2">
                            Ajouter un créneau
                        </Button>
                    </FormSection>
                )}

                {(error || (editing && onDelete)) && (
                    <FormSection className="flex flex-wrap items-center justify-between gap-3">
                        {error ? <p className="text-sm text-red-500">{error}</p> : <span/>}
                        {editing && onDelete && (
                            <Button variant="danger" size="sm" icon={Trash2} onClick={onDelete} disabled={saving}>
                                Supprimer
                            </Button>
                        )}
                    </FormSection>
                )}
            </FormSections>
        </Modal>
    );
};

// ─── Appointment: cancel (keeps the RDV, frees the slot) ───────────────────────

const CANCEL_REASONS = [
    'Client indisponible',
    'Client a annulé',
    'Erreur de saisie',
    'Changement de date/heure',
    'Double réservation',
    'Autre',
];

export const CancelEventModal = ({event, onConfirm, onCancel}) => {
    const [reason, setReason] = useState('');
    const [notify, setNotify] = useState(Boolean(event.clientInfo?.phone));

    return (
        <Modal
            title="Annuler le rendez-vous"
            subtitle={event.clientName || event.title}
            onClose={onCancel}
            size="md"
            bodyClass=""
            footer={<ModalFooter onCancel={onCancel} cancelLabel="Retour" label="Annuler le RDV"
                                 tone="text-red-600 dark:text-red-400"
                                 onConfirm={() => onConfirm({reason: reason.trim(), notify})}/>}
        >
            <SlotSummary
                color={PROFESSIONAL_ACCENT_RED}
                icon={CalendarX2}
                title={capitalize(event.start.toLocaleDateString('fr-FR', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'}))}
                subtitle={`${toTimeInput(event.start)} – ${toTimeInput(event.end)}${event.clientName ? ` · ${event.clientName}` : ''}`}
                status={event.status}
            />
            <FormSections>
                <FormSection className="space-y-3">
                    <SectionTitle icon={StickyNote}>Motif</SectionTitle>
                    <div className="flex flex-wrap gap-1.5">
                        {CANCEL_REASONS.map(r => (
                            <button key={r} type="button" aria-pressed={reason === r} onClick={() => setReason(reason === r ? '' : r)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                        reason === r
                                            ? 'bg-primary text-white shadow-sm shadow-primary/30'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary'
                                    }`}>
                                {r}
                            </button>
                        ))}
                    </div>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                              className={`${fieldClass} resize-none`} placeholder="Précisez le motif (facultatif)..."/>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Le motif est ajouté en tête de la remarque du RDV, comme sur les tablettes.
                    </p>
                </FormSection>
                <FormSection>
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
                               disabled={!event.clientInfo?.phone}
                               className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"/>
                        <span className="flex-1 text-sm text-gray-700 dark:text-gray-300">
                            <span className="flex items-center gap-1.5 font-medium">
                                <MessageCircle className="w-4 h-4 text-primary"/>Prévenir le client par WhatsApp
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400">
                                {event.clientInfo?.phone ? `Message d'annulation envoyé au ${event.clientInfo.phone}` : "Ce client n'a pas de numéro de téléphone"}
                            </span>
                        </span>
                    </label>
                </FormSection>
            </FormSections>
        </Modal>
    );
};

// ─── Appointment: delete ───────────────────────────────────────────────────────

export const DeleteConfirmModal = ({event, onConfirm, onCancel}) => (
    <Modal
        title="Supprimer le rendez-vous"
        onClose={onCancel}
        size="sm"
        footer={<ModalFooter onCancel={onCancel} onConfirm={onConfirm} label="Supprimer" tone="text-red-600 dark:text-red-400"/>}
    >
        <div className="flex flex-col items-center text-center mb-4">
            <span className="w-12 h-12 rounded-full bg-red-500/10 text-red-600 dark:text-red-400 flex items-center justify-center mb-3">
                <Trash2 className="w-5 h-5"/>
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-300">
                Êtes-vous sûr de vouloir supprimer ce rendez-vous ? Il restera récupérable dans la corbeille.
            </p>
        </div>
        <div className={`relative overflow-hidden rounded-xl border pl-4 pr-3 py-3 ${statusStyle(event.status).card}`}>
            <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary"/>
            <div className="font-semibold">{event.clientName || event.title}</div>
            {event.clientName && event.title && event.title !== event.clientName && (
                <div className="text-sm opacity-80 mt-0.5">{event.title}</div>
            )}
            <div className="flex items-center gap-1.5 text-sm opacity-75 mt-1">
                <CalendarDays className="w-3.5 h-3.5"/>
                {capitalize(event.start.toLocaleString('fr-FR', {
                    weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
                }))}
            </div>
        </div>
    </Modal>
);
